import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import FactoryPageHeader from '../../components/factory/FactoryPageHeader';
import MachineMiniCard, { machineNumber } from '../../components/factory/MachineMiniCard';
import { fetchFactories, fetchMachineDashboard, fetchFactoryLines } from '../../services/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useTranslation } from '../../context/LanguageContext';
import './FactoryMachineOverview.css';

/* ===== 공장 모니터링 > 사출기 현황 — PNG 시안「전사 > 한국 공장 > 사출기 현황 - 접힘」=====
 * 헤더: 전사 › [공장 ▾] › 사출기 현황 + 동기화 시각 · 새로 고침 (components/factory/FactoryPageHeader)
 * 패널: "공장내 사출기별 현황 · n대" + 라인 / 상태 / 정렬 셀렉트, 5열 카드 그리드
 * 카드(접힘): 큰 번호(설비명 끝 숫자), 상태 아이콘(가동 ▷ / 대기 ‖ / 정지 □), 알람 수 배지, 금형번호, 샷카운트.
 *            알람이 있으면 빨간 테두리·번호. 누르면 사출기 대시보드, 알람 배지를 누르면 알람 이력으로 간다.
 *
 * 데이터: /dashboard/machines/{factoryId}[/{lineId}] (MachineCard) — name, status(running/idle/stopped), alarms(활성 알람 수),
 *         craftId(제품·금형 ID → "금형번호"), shotNo(샷카운트), utilization(가동률, 24시간 타임라인 기준), cycleTime.
 *         가동률·사이클타임은 카드에 자리가 없어 카드 툴팁으로 보여 준다. 스타일은 fm- 접두어. */

const STATUS_FILTERS = ['all', 'running', 'idle', 'stopped', 'alarm'];
const SORTS = ['utilization', 'number', 'shot', 'alarm'];

const isNum = (v) => v != null && v !== '' && !Number.isNaN(Number(v));

const STATUS_ICON = { running: 'play_circle', idle: 'pause_circle', stopped: 'stop_circle' };

function FactoryMachineOverview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const tr = useCallback((k, p) => t(`pages.overview.factoryMachine.${k}`, p), [t]);

  const [factories, setFactories] = useState([]);
  const [factoryId, setFactoryId] = useState(null);
  const [lines, setLines] = useState([]);
  const [lineId, setLineId] = useState(null);
  const [machines, setMachines] = useState([]);
  const [syncTime, setSyncTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('number');

  const factoryIdRef = useRef(null);
  const lineIdRef = useRef(null);

  /* 1. 공장 목록 — URL factoryId 우선, 없으면 첫 공장 */
  useEffect(() => {
    let cancelled = false;
    fetchFactories()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setFactories(list);
        const fromUrl = parseInt(searchParams.get('factoryId'), 10);
        setFactoryId(list.some((f) => f.id === fromUrl) ? fromUrl : (list[0]?.id ?? null));
        if (list.length === 0) setLoading(false);
      })
      .catch((err) => {
        console.error('공장 목록 조회 실패:', err);
        if (!cancelled) { setError(tr('factoryListLoadFailed')); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, [searchParams, tr]);

  /* 2. 선택 공장의 라인 목록 — URL lineId 가 있으면 그 라인 */
  useEffect(() => {
    if (!factoryId) return;
    fetchFactoryLines(factoryId)
      .then((list) => {
        setLines(list || []);
        const fromUrl = parseInt(searchParams.get('lineId'), 10);
        setLineId(Number.isNaN(fromUrl) ? null : fromUrl);
      })
      .catch((err) => { console.error('라인 목록 조회 실패:', err); setLines([]); setLineId(null); });
  }, [factoryId, searchParams]);

  /* 3. 사출기 목록 */
  const load = useCallback(async (silent = false) => {
    const fid = factoryIdRef.current;
    if (!fid) return;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const data = await fetchMachineDashboard(fid, lineIdRef.current);
      setMachines(data?.machines || []);
      setSyncTime(data?.lastSyncTime || new Date());
      setError(null);
    } catch (err) {
      console.error('사출기 데이터 조회 실패:', err);
      if (!silent) setError(tr('loadFailed'));
    } finally {
      if (silent) setTimeout(() => setRefreshing(false), 600); else setLoading(false);
    }
  }, [tr]);

  useEffect(() => {
    if (!factoryId) return;
    factoryIdRef.current = factoryId;
    lineIdRef.current = lineId;
    load(false);
  }, [factoryId, lineId, load]);

  useAutoRefresh(() => load(true), !!factoryId);

  /* 상태 필터 + 정렬 */
  const visible = useMemo(() => {
    const list = machines.filter((m) => {
      if (statusFilter === 'all') return true;
      if (statusFilter === 'alarm') return Number(m.alarms) > 0;
      return m.status === statusFilter;
    });
    const num = (m) => machineNumber(m.name) ?? Number.MAX_SAFE_INTEGER;
    const by = {
      utilization: (a, b) => (Number(b.utilization) || 0) - (Number(a.utilization) || 0) || num(a) - num(b),
      number: (a, b) => num(a) - num(b),
      shot: (a, b) => (Number(b.shotNo) || 0) - (Number(a.shotNo) || 0) || num(a) - num(b),
      alarm: (a, b) => (Number(b.alarms) || 0) - (Number(a.alarms) || 0) || num(a) - num(b)
    }[sortKey];
    return [...list].sort(by);
  }, [machines, statusFilter, sortKey]);

  const statusLabel = (s) => (STATUS_ICON[s] ? tr(`status.${s}`) : tr('status.error'));

  const cardTitle = (m) => [
    m.name,
    statusLabel(m.status),
    isNum(m.utilization) ? `${tr('cards.utilization')} ${Number(m.utilization).toFixed(1)}%` : null,
    isNum(m.cycleTime) && Number(m.cycleTime) > 0 ? `${tr('cards.cycleTime')} ${Number(m.cycleTime).toFixed(1)}s` : null,
    Number(m.alarms) > 0 ? tr('cards.alarmCount', { n: m.alarms }) : null
  ].filter(Boolean).join(' · ');

  const renderBody = () => {
    if (loading) return <div className="fm-message">{tr('loadingData')}</div>;
    if (error) {
      return (
        <div className="fm-message fm-message-error">
          {error}
          <button type="button" className="fm-retry" onClick={() => load(false)}>{tr('refresh')}</button>
        </div>
      );
    }
    if (visible.length === 0) return <div className="fm-message">{tr('emptyMachines')}</div>;
    return (
      <div className="fm-grid">
        {visible.map((m, idx) => (
          <MachineMiniCard
            key={m.id}
            name={m.name}
            fallbackNo={idx + 1}
            status={m.status}
            alarms={m.alarms}
            moldNo={m.craftId || m.productName}
            shotCount={isNum(m.shotNo) ? Number(m.shotNo) : null}
            moldLabel={tr('cards.moldNo')}
            shotLabel={tr('cards.shotCount')}
            cavity={m.cavityCount} /* GET /dashboard/machines → cavityCount (최근 샷 mold_cavity, 없으면 설비 등록값) */
            cycleTime={m.cycleTime} /* 같은 응답 cycleTime (초, 최근 샷) */
            cavLabel={tr('cards.cav')}
            ctLabel={tr('cards.ct')}
            statusLabel={statusLabel(m.status)}
            alarmTitle={tr('cards.alarmCount', { n: m.alarms })}
            title={cardTitle(m)}
            onClick={() => navigate('/machine', { state: { machineData: m } })}
            onAlarmClick={() => navigate('/machine', { state: { machineData: m, openTab: 'alarm' } })}
          />
        ))}
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="fm-page">
        <FactoryPageHeader
          rootLabel={tr('breadcrumbRoot')}
          factories={factories}
          selectedFactoryId={factoryId}
          onSelectFactory={(id) => { setLineId(null); setFactoryId(id); }}
          pageLabel={tr('pageLabel')}
          syncTime={syncTime}
          onRefresh={() => load(true)}
          refreshTitle={tr('refresh')}
          refreshing={refreshing}
        />

        <section className="fm-panel">
          <div className="fm-toolbar">
            <h2 className="fm-title">{tr('panelTitle', { n: machines.length })}</h2>
            <label className="fm-field">
              <span>{tr('cards.line')}</span>
              <select className="fm-select" value={lineId ?? ''} onChange={(e) => setLineId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">{tr('all')}</option>
                {lines.map((l) => <option key={l.id} value={l.id}>{l.lineName}</option>)}
              </select>
            </label>
            <label className="fm-field">
              <span>{tr('cards.state')}</span>
              <select className="fm-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                {STATUS_FILTERS.map((s) => <option key={s} value={s}>{s === 'all' ? tr('all') : s === 'alarm' ? tr('cards.alarmOnly') : statusLabel(s)}</option>)}
              </select>
            </label>
            <label className="fm-field">
              <span>{tr('cards.sort')}</span>
              <select className="fm-select" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                {SORTS.map((s) => <option key={s} value={s}>{tr(`cards.sort_${s}`)}</option>)}
              </select>
            </label>
          </div>
          {renderBody()}
        </section>
      </div>
    </MainLayout>
  );
}

export default FactoryMachineOverview;
